/**
 * Legal Case Tracker V2 — a focused list of cases that have escalated
 * past arrears (stage = bad_debt / written_off, or a `legal_stage` set).
 *
 * Mirrors the LoanAgeing list-page conventions:
 *   • KPI tiles across the top (total cases / total balance / by-stage)
 *   • Filter bar (handler, legal_stage, jurisdiction, search)
 *   • Compact table — click a row to open the case file
 *
 * Unlike the arrears page, this list is NOT scoped to today's CSV
 * report — legal cases stay here until ops mark them fully_paid /
 * written_off / back-to-collections.
 *
 * Phase 1 scope (this file):
 *   • Read-only list with filters + KPI tiles
 *   • Manual "Sync from Airtable" button (one-off import)
 *   • Row click → /legal/case/:case_id?source=legal (reuses CustomerFile)
 *
 * Phase 2 will add:
 *   • Legal Schedule centre-tab (DN expiry, SD2 hearing, etc.)
 *   • Legal Docs centre-tab
 *   • Stage edit dropdown wired to /cases/{id} PATCH
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { UnmatchedFoldersPanel } from "./UnmatchedFoldersPanel";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Scale, Search, RefreshCw, Loader2, AlertTriangle, ArrowRight,
  FileText, Filter, Calendar as CalIcon,
  Plus, Landmark, Archive, Building2, Signpost, PoundSterling,
  ArrowUp, ArrowDown, ArrowUpDown, MoreHorizontal, User as UserIcon, AlarmClock,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import {
  listLegalCases, listLegalStages, getAgeingHandlers, sortNames, listLegalEventTypes,
  syncLegalCasesFromLoanDisk, getLegalLoanDiskSyncStatus,
  startBulkAirtableSync, getBulkAirtableSyncStatus, getLatestBulkAirtableSync,
  triggerOneDriveSync, getOneDriveSyncStatus, bulkAssignAgeingCases,
  getLegalAutoRefreshLatest, getLegalNotesBundle, getLegalProcessMe,
} from "@/lib/api";
import { toast } from "sonner";
import UnassignedLegalPanel from "./UnassignedLegalPanel";
import { CourtReferenceInline } from "./CourtReferenceInline";
import { LegalDeskBanner, useLegalDesk } from "./legalDesk";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import FindLegalCaseDialog from "./FindLegalCaseDialog";
import { useAccountStatuses } from "@/components/collections/StatusTags";
import { JourneyLine } from "@/components/collections/JourneyLine";
import { HandToLegalButton } from "@/components/collections/JourneyActions";
import { TrackerToolsMenu } from "./TrackerToolsMenu";
import { OwnersCell } from "./OwnersCell";
import { FilterBar } from "@/components/filters/FilterBar";
import { ViewTabs } from "@/components/filters/ViewTabs";
import { useListView } from "@/components/filters/useListView";
import { applyFilter } from "@/components/filters/filterEngine";
import { trackerFields } from "./trackerFields";
import { LivePill } from "./LivePill";
import { Switch } from "@/components/ui/switch";
import { useIsPhone } from "@/hooks/useIsPhone";
import { PhoneLegalTracker } from "./phone/PhoneLegalTracker";

const fmtGBP = (n) =>
  `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

// Emma #1 — filters survive navigating into a case and back (per browser tab).
const LEGAL_FILTERS_SS_KEY = "legal-tracker-filters-v2";
// Seven-column tracker (Dean, 13 Oct 2026): Customer+SF · Journey · Owners · Next action · Balance — Airtable-style grid so the team can switch over easily
const AT_COLUMNS = [
  ["customer", "Customer", "text-left", Building2, "min-w-[260px]"],
  ["journey", "Journey", "text-left", Signpost, "min-w-[260px]"],
  ["owners", "Owners", "text-left", UserIcon, "w-[180px]"],
  ["next_action", "Next action", "text-left", AlarmClock, "min-w-[240px]"],
  ["balance", "Balance", "text-right", PoundSterling, "w-[120px]"],
];
const NEXT_TONE = {
  breached: "bg-rose-600 text-white border-rose-700",
  red:      "bg-red-50 text-red-900 border-red-300",
  amber:    "bg-amber-50 text-amber-900 border-amber-300",
  green:    "bg-emerald-50 text-emerald-900 border-emerald-300",
  upcoming: "bg-white text-slate-800 border-slate-200",
  none:     "",
};
const NEXT_DOT = { breached: "bg-white", red: "bg-red-600", amber: "bg-amber-500", green: "bg-emerald-500", upcoming: "bg-slate-300", none: "" };
const loadLegalSaved = () => {
  try { return JSON.parse(sessionStorage.getItem(LEGAL_FILTERS_SS_KEY) || "null") || {}; } catch { return {}; }
};

export default function LegalCaseTrackerV2() {
  const navigate = useNavigate();
  useLegalDesk();
  const isPhone = useIsPhone();
  const [cases, setCases]               = useState([]);
  const [summary, setSummary]           = useState({});
  const [stages, setStages]             = useState([]);
  const [handlers, setHandlers]         = useState([]);          // Legal group — owner actions
  const [accountHandlers, setAccountHandlers] = useState([]);   // Collections group — account handler bulk assign
  const [loading, setLoading]           = useState(true);
  const [legalMe, setLegalMe]           = useState(null);   // A1: is_legal_head → Unassigned panel
  useEffect(() => { getLegalProcessMe().then(setLegalMe).catch(() => setLegalMe({ is_legal_head: false })); }, []);

  // Filters — jurisdiction removed (item 13), actions_within_days added
  const [search, setSearch]                 = useState(() => loadLegalSaved().search || "");
  const [handlerFilter, setHandlerFilter]   = useState(() => loadLegalSaved().handlerFilter || "all");
  const [actionsWithinDays, setActionsWithinDays] = useState(() => loadLegalSaved().actionsWithinDays ?? null);  // null = inactive; integer = N days
  // Two-axis model (Dean, 13 Oct 2026): lanes + Needs attention + Closed replace the Review / Hold / BS / No-next-action chips
  const [lane, setLane]                     = useState(() => loadLegalSaved().lane || null);              // handover | chase | legal_steps | paused | null
  const [attentionOnly, setAttentionOnly]   = useState(() => loadLegalSaved().attentionOnly || false);
  // Live legal (Dean, 15 Oct 2026): the default list is files with legal work genuinely in progress; the switch brings history back
  const [includeHistory, setIncludeHistory] = useState(() => loadLegalSaved().includeHistory || false);
  const [eventTypes, setEventTypes]         = useState([]);
  const accountStatuses = useAccountStatuses();
  // Dean, 8 Oct 2026 — quick views (client-side on the loaded set) + sort
  const { user: authUser } = useAuth();
  const myName = authUser?.name || "";
  const [quickView, setQuickView] = useState(() => loadLegalSaved().quickView || null);     // overdue | hearings_month | null
  const [sort, setSort] = useState(() => loadLegalSaved().sort || { key: "next_action", dir: "asc" });

  // Bulk handler assignment (item 12)
  const [selectedIds, setSelectedIds]   = useState(() => new Set());
  const [bulkBusy, setBulkBusy]         = useState(false);

  // Find Legal Case dialog (manual LoanDisk-search escalation)
  const [findOpen, setFindOpen]         = useState(false);
  const [bundleCase, setBundleCase]     = useState(null); // court-bundle viewer (Emma 7cb2874553)

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (handlerFilter !== "all")            params.handler             = handlerFilter;
      if (actionsWithinDays !== null)         params.actions_within_days = actionsWithinDays;
      if (lane)                               params.lane                = lane;
      if (attentionOnly)                      params.attention           = true;
      params.scope = includeHistory ? "all" : "live";
      if (search.trim())                      params.search              = search.trim();

      const r = await listLegalCases(params);
      setCases(r.cases || []);
      setSummary({
        total:           r.total_in_legal || 0,
        total_balance:   r.total_balance  || 0,
        by_stage:        r.by_stage       || {},
        by_handler:      r.by_handler     || {},
        handler_options: r.handler_options || [],
        lanes:           r.lanes          || {},
        attention_count: r.attention_count || 0,
        tracker_count:   r.tracker_count  || 0,
        closed_count:    r.closed_count   || 0,
        live_count:      r.live_count     || 0,
        history_count:   r.history_count  || 0,
        live_breakdown:  r.live_breakdown || {},
      });
    } catch (e) {
      toast.error("Failed to load legal cases: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  }, [handlerFilter, actionsWithinDays, lane, attentionOnly, includeHistory, search]);

  // One-time refs — stages + handlers don't change between sessions
  // Dropdowns are alphabetical (Dean, 8 Oct 2026); handlers = Legal group for owner actions
  useEffect(() => {
    (async () => {
      try {
        const [s, h, ah, et] = await Promise.all([listLegalStages(), getAgeingHandlers("legal"), getAgeingHandlers("collections").catch(() => ({ handlers: [] })), listLegalEventTypes().catch(() => ({ types: [] }))]);
        setStages(s.stages || []);
        setHandlers(sortNames(h.handlers));
        setAccountHandlers(sortNames(ah.handlers));
        setEventTypes([...(et.types || [])].sort((a, b) => (a.label || "").localeCompare(b.label || "", "en", { sensitivity: "base" })));
      } catch (e) {
        // Non-fatal — case list still renders, just the dropdowns will be empty
        console.warn("Failed to load reference data:", e);
      }
    })();
  }, []);
  const stagesAlpha = useMemo(() => [...stages].sort((a, b) => (a.label || "").localeCompare(b.label || "", "en", { sensitivity: "base" })), [stages]);
  const statusesAlpha = useMemo(() => [...accountStatuses].sort((a, b) => (a.label || "").localeCompare(b.label || "", "en", { sensitivity: "base" })), [accountStatuses]);
  const courts = useMemo(() => [...new Set(cases.map((c) => c.court_name || c.chosen_court?.name).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [cases]);
  const fields = useMemo(() => trackerFields({ stages: stagesAlpha, handlers, accountHandlers: summary.handler_options || [], statuses: statusesAlpha, eventTypes, courts }),
    [stagesAlpha, handlers, summary.handler_options, statusesAlpha, eventTypes, courts]);

  // Re-fetch whenever filters change (debounced via the dependency array)
  useEffect(() => {
    const t = setTimeout(reload, 250);
    return () => clearTimeout(t);
  }, [reload]);

  // ─── Unified "Refresh All" — orchestrates the two heavy sync jobs ──
  // Phase 1: LoanDisk legal-loans sync (1-3 min — paginates the whole loan
  //          book and upserts collections_cases for loan_status_id=3).
  // Phase 2: Bulk Airtable sync (~3 sec — pulls every CaseNotes blob and
  //          materialises timeline notes + deadlines + calendar events + P2Ps).
  //
  // Why front-end orchestration: both phases already have their own
  // background-task + polling infrastructure on the backend; chaining
  // them client-side keeps the backend simple and avoids a third
  // state-machine. The progress modal shows the current phase + per-phase
  // counters so ops can see both halves of the operation.
  const [refreshOpen, setRefreshOpen]       = useState(false);
  const [refreshRunning, setRefreshRunning] = useState(false);
  const [refreshPhase, setRefreshPhase]     = useState(null);   // 'loandisk' | 'airtable' | 'onedrive' | 'done' | 'failed'
  const [loanDiskState, setLoanDiskState]   = useState(null);   // the /sync-loandisk/status snapshot
  const [bulkSyncJob, setBulkSyncJob]       = useState(null);   // the /bulk-sync-airtable job snapshot
  const [oneDriveState, setOneDriveState]   = useState(null);   // the /sync-onedrive/status snapshot
  const [refreshError, setRefreshError]     = useState(null);

  // Recover any in-flight bulk Airtable job on mount (page-refresh-safe).
  // We don't auto-resume the LoanDisk phase because its singleton state
  // is in-memory only — a backend restart wipes it.
  useEffect(() => {
    (async () => {
      try {
        const r = await getLatestBulkAirtableSync();
        if (r?.job && r.job.status === "running") {
          setBulkSyncJob(r.job);
          setRefreshPhase("airtable");
          setRefreshRunning(true);
          setRefreshOpen(true);
        }
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Poll loop — interval picks the right endpoint based on the current
  // phase. Stops when the phase transitions to 'done' or 'failed'.
  useEffect(() => {
    if (!refreshRunning) return undefined;
    const timer = setInterval(async () => {
      try {
        if (refreshPhase === "loandisk") {
          const r = await getLegalLoanDiskSyncStatus();
          setLoanDiskState(r);
          if (r?.state === "completed") {
            // Phase 1 done — kick off phase 2.
            setRefreshPhase("airtable");
            try {
              const start = await startBulkAirtableSync();
              const jobId = start.job_id || start.already_running?.job_id;
              if (jobId) {
                const status = await getBulkAirtableSyncStatus(jobId);
                setBulkSyncJob(status.job);
              }
            } catch (e) {
              setRefreshError("Airtable phase failed to start: " + (e?.response?.data?.detail || e?.message));
              setRefreshPhase("failed");
              setRefreshRunning(false);
            }
          } else if (r?.state === "failed") {
            setRefreshError(r?.error || "LoanDisk sync failed");
            setRefreshPhase("failed");
            setRefreshRunning(false);
          }
        } else if (refreshPhase === "airtable" && bulkSyncJob?.job_id) {
          const r = await getBulkAirtableSyncStatus(bulkSyncJob.job_id);
          const job = r?.job;
          if (!job) return;
          setBulkSyncJob(job);
          if (job.status === "complete" || job.status === "failed") {
            if (job.status === "complete") {
              // Phase 2 done → kick off Phase 3 (OneDrive)
              setRefreshPhase("onedrive");
              try {
                await triggerOneDriveSync();
              } catch (e) {
                // Non-fatal — surface the error but still mark refresh done
                console.warn("OneDrive phase failed to start:", e);
                setRefreshPhase("done");
                setRefreshRunning(false);
                reload();
              }
            } else {
              setRefreshError(job.error || "Airtable bulk sync failed");
              setRefreshPhase("failed");
              setRefreshRunning(false);
            }
          }
        } else if (refreshPhase === "onedrive") {
          const r = await getOneDriveSyncStatus();
          setOneDriveState(r);
          if (r?.state === "completed" || r?.state === "failed") {
            setRefreshRunning(false);
            if (r.state === "completed") {
              const t = bulkSyncJob?.totals || {};
              const ldIm  = (loanDiskState?.imported || 0);
              const ldUp  = (loanDiskState?.updated  || 0);
              setRefreshPhase("done");
              toast.success(
                `Refresh complete — LoanDisk: ${ldIm} new, ${ldUp} updated. ` +
                `Airtable: ${t.imported_count || 0} notes, ${t.deadline_count || 0} deadlines, ` +
                `${t.calendar_events_created || 0} cal events, ${t.p2ps_created || 0} P2Ps, ` +
                `${t.cases_created || 0} new cases. ` +
                `OneDrive: ${r.matched} matched, ${r.unmatched} unmatched.`,
                { duration: 12000 },
              );
              reload();
            } else {
              setRefreshError((r.errors || []).join("; ") || "OneDrive sync failed");
              setRefreshPhase("failed");
            }
          }
        }
      } catch (e) {
        console.warn("refresh poll error:", e);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [refreshRunning, refreshPhase, bulkSyncJob?.job_id, loanDiskState?.imported, loanDiskState?.updated, reload]);

  const handleRefreshAll = async () => {
    setRefreshError(null);
    setLoanDiskState(null);
    setBulkSyncJob(null);
    setRefreshOpen(true);
    setRefreshPhase("loandisk");
    setRefreshRunning(true);
    try {
      // Kick off LoanDisk phase (fire-and-forget; the poll loop drives
      // the rest of the orchestration).
      await syncLegalCasesFromLoanDisk();
    } catch (e) {
      setRefreshError("LoanDisk sync failed to start: " + (e?.response?.data?.detail || e?.message));
      setRefreshPhase("failed");
      setRefreshRunning(false);
    }
  };

  // ─── Bulk handler assignment (item 12) — selection helpers live below `visibleCases` ──
  const toggleRow = (caseId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };
  // Reset selection whenever the visible set changes
  useEffect(() => { setSelectedIds(new Set()); }, [handlerFilter, actionsWithinDays, search, includeHistory]);

  // Emma #1 — persist the active filters for this browser tab
  useEffect(() => {
    try {
      sessionStorage.setItem(LEGAL_FILTERS_SS_KEY, JSON.stringify({
        search, handlerFilter, actionsWithinDays, lane, attentionOnly, includeHistory, quickView, sort,
      }));
    } catch { /* ignore */ }
  }, [search, handlerFilter, actionsWithinDays, lane, attentionOnly, includeHistory, quickView, sort]);

  // Saved views (Dean, 15 Oct 2026): filter tree + sort + the quick toggles, personal or shared, pinned = team default; URL carries ?view= / ?f=
  const lv = useListView({
    page: "legal_tracker", region: "UK",
    snapshot: () => ({ sort, lane, attentionOnly, quickView, handlerFilter, includeHistory, actionsWithinDays }),
    restore: (st) => {
      const s = st || {};
      setSort(s.sort || { key: "next_action", dir: "asc" }); setLane(s.lane || null); setAttentionOnly(!!s.attentionOnly); setQuickView(s.quickView || null);
      setHandlerFilter(s.handlerFilter || "all"); setIncludeHistory(!!s.includeHistory); setActionsWithinDays(s.actionsWithinDays ?? null);
    },
  });

  // Dean, 8 Oct 2026 — quick views + sort are applied client-side on the loaded set (≤500 rows, instant)
  const monthKey = new Date().toISOString().slice(0, 7);
  const visibleCases = useMemo(() => {
    let rows = applyFilter(cases, lv.tree, fields, { me: myName });
    if (quickView === "overdue") rows = rows.filter(c => (c.next_event?.days_overdue || 0) > 0);
    if (quickView === "hearings_month") rows = rows.filter(c => (c.hearing?.date || "").startsWith(monthKey));
    const dir = sort.dir === "desc" ? -1 : 1;
    const str = (v) => (v || "").toString().toLowerCase();
    const cmp = {
      customer:    (a, b) => str(a.customer_name).localeCompare(str(b.customer_name)),
      journey:     (a, b) => str(a.journey?.line).localeCompare(str(b.journey?.line)),
      next_action: (a, b) => (a.next_event?.date || "9999").localeCompare(b.next_event?.date || "9999"),
      owners:      (a, b) => str(a.legal_handler || a.handler).localeCompare(str(b.legal_handler || b.handler)),
      latest_note: (a, b) => (b.latest_human_note?.at || "").localeCompare(a.latest_human_note?.at || ""),
      handler:     (a, b) => str(a.legal_handler || a.handler || "zzz").localeCompare(str(b.legal_handler || b.handler || "zzz")),
      account:     (a, b) => str(a.handler || "zzz").localeCompare(str(b.handler || "zzz")),
      legal_owner: (a, b) => str(a.legal_handler || a.handler || "zzz").localeCompare(str(b.legal_handler || b.handler || "zzz")),
      balance:     (a, b) => (a.balance_amount || 0) - (b.balance_amount || 0),
    }[sort.key] || (() => 0);
    const sorted = [...rows].sort((a, b) => cmp(a, b) * dir);
    if (sort.key === "next_action") {
      // files with nothing diarised sink to the bottom regardless of direction, biggest balance first
      const withEv = sorted.filter(c => c.next_event), without = sorted.filter(c => !c.next_event).sort((a, b) => (b.balance_amount || 0) - (a.balance_amount || 0));
      return [...withEv, ...without];
    }
    return sorted;
  }, [cases, quickView, sort, monthKey, lv.tree, fields, myName]);
  const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "balance" || key === "latest_note" ? "desc" : "asc" });
  const SortIcon = ({ k }) => sort.key !== k ? <ArrowUpDown className="w-3 h-3 opacity-40" /> : sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  const allSelected = visibleCases.length > 0 && visibleCases.every(c => selectedIds.has(c.case_id));
  const someSelected = visibleCases.some(c => selectedIds.has(c.case_id));
  const toggleAllVisible = () => setSelectedIds(allSelected ? new Set() : new Set(visibleCases.map(c => c.case_id)));

  // Emma #7 — last nightly auto-refresh status chip
  const [autoRefreshRun, setAutoRefreshRun] = useState(null);
  useEffect(() => {
    getLegalAutoRefreshLatest().then(r => setAutoRefreshRun((r.runs || [])[0] || null)).catch(() => {});
  }, []);

  // Emma #3/#10 — export the filtered tracker view as CSV
  const exportCsv = () => {
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const head = ["Customer", "SF#", "Account", "Legal action", "Journey", "Legal owner", "Account handler", "Legal stage (ladder)", "Next action", "Next action due", "Days overdue",
                  "Latest note", "Balance £", "Hearing date", "Needs attention"];
    const rows = visibleCases.map(c => [
      c.customer_name || "", c.loan_application_id || "", c.journey?.account?.label || "", c.journey?.legal?.label || "", c.journey?.line || "",
      c.legal_handler || "", c.handler || "",
      (stages.find(s => s.key === c.legal_stage)?.label) || humanise(c.legal_stage) || "",
      c.next_event?.label || "", c.next_event?.date || "",
      c.next_event?.days_overdue > 0 ? c.next_event.days_overdue : "",
      c.latest_human_note?.text || "", c.balance_amount ?? "",
      c.hearing?.date || "", (c.journey?.attention || []).map(a => a.label).join("; "),
    ]);
    const csv = [head, ...rows].map(r => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `legal-case-tracker-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${rows.length} case${rows.length === 1 ? "" : "s"} to CSV`);
  };

  const handleBulkAssign = async (newHandler, field = "legal_handler") => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const r = await bulkAssignAgeingCases({
        case_ids: ids,
        handler: newHandler,
        field,
      });
      toast.success(
        `${field === "legal_handler" ? "Legal owner" : "Account handler"} → ${r.new_handler} on ${r.changed} case${r.changed === 1 ? "" : "s"}`,
      );
      setSelectedIds(new Set());
      reload();
    } catch (e) {
      toast.error("Bulk assign failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBulkBusy(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────
  if (isPhone) {
    return <PhoneLegalTracker lv={lv} fields={fields} cases={visibleCases} loading={loading} summary={summary} search={search} setSearch={setSearch} includeHistory={includeHistory} setIncludeHistory={setIncludeHistory} />;
  }
  return (
    <TooltipProvider delayDuration={150}>
    <div className="p-4 space-y-3 max-w-[1600px] mx-auto" data-testid="legal-case-tracker-v2">
      <LegalDeskBanner />
      {/* Item 10: Compact header — title + inline KPI strip + actions cluster
          (mirrors LoanAgeing's "title · count · balance · upload" pattern). */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <Scale className="w-5 h-5 text-red-700 shrink-0" />
          <h1 className="text-base font-semibold whitespace-nowrap">Legal Case Tracker</h1>
          {!loading && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0" data-testid="legal-summary-hero">
              <span className="text-slate-400">·</span>
              <span className="whitespace-nowrap" title={includeHistory ? "Every file with legal history — live, tidy-up, history and closed" : "Live legal — a live route or open step, pre-action out, or an undone legal action in the diary"}>
                <span className="font-semibold text-indigo-900 tabular-nums" data-testid="legal-kpi-total-cases">{summary.total || 0}</span>
                <span className="text-slate-500 ml-1">{includeHistory ? "files incl. history" : "live legal"}</span>
              </span>
              <span className="whitespace-nowrap">
                <span className="font-semibold text-rose-700 tabular-nums" data-testid="legal-kpi-balance">{fmtGBP(summary.total_balance)}</span>
                <span className="text-slate-500 ml-1">at risk</span>
              </span>
              <span className="whitespace-nowrap">
                <span className="font-semibold text-amber-800 tabular-nums" data-testid="legal-kpi-unassigned">{summary.by_handler?.Unassigned || 0}</span>
                <span className="text-slate-500 ml-1">no legal owner</span>
              </span>
              <span className="whitespace-nowrap" title="Files with at least one Needs-attention line (overdue handover · LoanDisk disagrees · no legal owner · nothing diarised · restructured with legal pending · paused past review)">
                <span className={`font-semibold tabular-nums ${summary.attention_count ? "text-orange-700" : "text-emerald-700"}`} data-testid="legal-kpi-attention">{summary.attention_count || 0}</span>
                <span className="text-slate-500 ml-1">need attention</span>
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Noise pass (Dean, 13 Oct 2026): three controls — everything else is in Tools */}
          <TrackerToolsMenu
            canPublish={!!legalMe?.can_publish} isLegalHead={!!legalMe?.is_legal_head}
            onExportCsv={exportCsv} exportDisabled={cases.length === 0}
            onRefreshAll={handleRefreshAll} refreshRunning={refreshRunning} autoRefreshRun={autoRefreshRun}
            onCasesChanged={reload}
          />
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => navigate("/legal/calendar")}
            data-testid="legal-open-calendar"
            title="Open the Legal Calendar — all DN/SD2/hearing dates across every case."
          >
            <CalIcon className="w-3.5 h-3.5 mr-1.5" />
            Legal Calendar
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs bg-red-700 hover:bg-red-800 text-white"
            onClick={() => setFindOpen(true)}
            data-testid="legal-find-case"
            title="Search LoanDisk by SF number or customer name and hand that loan to Legal — LoanDisk → Legal, bankruptcy route starts at Eligibility check."
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add legal case
          </Button>
        </div>
      </div>

      {/* Saved views — tabs; the pinned team default is first and marked (Dean, 15 Oct 2026) */}
      <ViewTabs lv={lv} allLabel="Live legal" testid="legal-views" />

      {/* A1 (Dean, 8 Oct 2026): Unassigned intake + caseload — Head of Legal / admins only */}
      {legalMe?.is_legal_head && <UnassignedLegalPanel stages={stages} onCasesChanged={reload} />}

      {/* Find Legal Case — LoanDisk search → escalate to Legal */}
      <FindLegalCaseDialog
        open={findOpen}
        onOpenChange={setFindOpen}
        onCaseCreated={() => reload()}
      />

      {/* Emma 7cb2874553 — court-bundle notes viewer straight from the tracker */}
      <CourtBundleDialog caseRow={bundleCase} onClose={() => setBundleCase(null)} />

      {/* Refresh All — unified progress modal (LoanDisk + Airtable phases) */}
      <Dialog open={refreshOpen} onOpenChange={(o) => { if (!refreshRunning) setRefreshOpen(o); }}>
        <DialogContent className="sm:max-w-lg" data-testid="legal-refresh-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 text-red-700 ${refreshRunning ? "animate-spin" : ""}`} />
              Bulk refresh — all legal data sources
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            {/* Phase 1 — LoanDisk */}
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-2">
                  <span className={
                    refreshPhase === "loandisk" ? "text-amber-700"
                    : (refreshPhase === "airtable" || refreshPhase === "done") ? "text-emerald-700"
                    : "text-slate-500"
                  }>
                    Phase 1: LoanDisk
                  </span>
                  {refreshPhase === "loandisk" && <Loader2 className="w-3 h-3 animate-spin text-amber-700" />}
                  {(refreshPhase === "airtable" || refreshPhase === "done") && <span className="text-emerald-700 text-xs">✓</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {loanDiskState?.state || (refreshPhase === "loandisk" ? "starting…" : "pending")}
                </div>
              </div>
              {loanDiskState && (
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Imported:</span> <span className="font-semibold">{loanDiskState.imported || 0}</span></div>
                  <div><span className="text-muted-foreground">Updated:</span> <span className="font-semibold">{loanDiskState.updated  || 0}</span></div>
                  <div><span className="text-muted-foreground">Skipped:</span> <span className="font-semibold">{loanDiskState.skipped  || 0}</span></div>
                  {loanDiskState.total_loandisk_legal_loans != null && (
                    <div className="col-span-3 text-[10px] text-muted-foreground">
                      {loanDiskState.total_loandisk_legal_loans} legal loans found in LoanDisk
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Phase 2 — Airtable */}
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-2">
                  <span className={
                    refreshPhase === "airtable" ? "text-amber-700"
                    : refreshPhase === "done" ? "text-emerald-700"
                    : "text-slate-500"
                  }>
                    Phase 2: Airtable CaseNotes
                  </span>
                  {refreshPhase === "airtable" && <Loader2 className="w-3 h-3 animate-spin text-amber-700" />}
                  {refreshPhase === "done" && <span className="text-emerald-700 text-xs">✓</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {bulkSyncJob?.status || (refreshPhase === "airtable" ? "starting…" : "pending")}
                </div>
              </div>
              {bulkSyncJob && (
                <>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground">
                      {bulkSyncJob.processed || 0} / {bulkSyncJob.total || "?"} records
                    </span>
                    <span className="tabular-nums">
                      {bulkSyncJob.total ? Math.round(100 * (bulkSyncJob.processed || 0) / bulkSyncJob.total) : 0}%
                    </span>
                  </div>
                  <Progress
                    value={bulkSyncJob.total ? (100 * (bulkSyncJob.processed || 0) / bulkSyncJob.total) : 0}
                    className="h-2 mb-2"
                  />
                  {bulkSyncJob.current_sf && bulkSyncJob.status === "running" && (
                    <p className="mb-2 text-[11px] text-muted-foreground">
                      Currently: <span className="font-mono">{bulkSyncJob.current_sf}</span>
                    </p>
                  )}
                  {bulkSyncJob.totals && (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div><span className="text-muted-foreground">Notes imported:</span> <span className="font-semibold">{bulkSyncJob.totals.imported_count || 0}</span></div>
                      <div><span className="text-muted-foreground">Deadlines:</span> <span className="font-semibold">{bulkSyncJob.totals.deadline_count || 0}</span></div>
                      <div><span className="text-muted-foreground">Calendar events:</span> <span className="font-semibold">{bulkSyncJob.totals.calendar_events_created || 0}</span></div>
                      <div><span className="text-muted-foreground">P2Ps:</span> <span className="font-semibold">{bulkSyncJob.totals.p2ps_created || 0}</span></div>
                      <div className="col-span-2"><span className="text-muted-foreground">New case records:</span> <span className="font-semibold">{bulkSyncJob.totals.cases_created || 0}</span></div>
                    </div>
                  )}
                  {bulkSyncJob.error_count > 0 && (
                    <div className="mt-2 text-[11px] text-rose-700">
                      {bulkSyncJob.error_count} record{bulkSyncJob.error_count === 1 ? "" : "s"} failed
                      ({(bulkSyncJob.errors || []).slice(-3).map(e => e.sf).join(", ")}…)
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Phase 3 — OneDrive metadata sync */}
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-2">
                  <span className={
                    refreshPhase === "onedrive" ? "text-amber-700"
                    : refreshPhase === "done" ? "text-emerald-700"
                    : "text-slate-500"
                  }>
                    Phase 3: OneDrive case files
                  </span>
                  {refreshPhase === "onedrive" && <Loader2 className="w-3 h-3 animate-spin text-amber-700" />}
                  {refreshPhase === "done" && <span className="text-emerald-700 text-xs">✓</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {oneDriveState?.state || (refreshPhase === "onedrive" ? "starting…" : "pending")}
                </div>
              </div>
              {oneDriveState && oneDriveState.state !== "idle" && (
                <>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                    <div><span className="text-muted-foreground">Processed:</span> <span className="font-semibold">{oneDriveState.processed || 0}</span></div>
                    <div><span className="text-muted-foreground">Matched:</span> <span className="font-semibold text-emerald-700">{oneDriveState.matched || 0}</span></div>
                    <div><span className="text-muted-foreground">Unmatched:</span> <span className="font-semibold text-amber-700">{oneDriveState.unmatched || 0}</span></div>
                  </div>
                  {oneDriveState.current && oneDriveState.state === "running" && (
                    <p className="text-[10px] font-mono text-muted-foreground truncate">
                      {oneDriveState.current}
                    </p>
                  )}
                  {oneDriveState.deleted > 0 && (
                    <p className="text-[11px] text-rose-700 mt-1">
                      {oneDriveState.deleted} files removed from OneDrive since last sync
                    </p>
                  )}
                  {(oneDriveState.errors || []).length > 0 && (
                    <div className="mt-2 text-[11px] text-rose-700">
                      {(oneDriveState.errors || []).length} error{oneDriveState.errors.length === 1 ? "" : "s"} during sync
                    </div>
                  )}
                  {oneDriveState.state === "completed" && (oneDriveState.unmatched || 0) > 0 && (
                    <div className="mt-2"><UnmatchedFoldersPanel region="UK" /></div>
                  )}
                </>
              )}
            </div>

            {refreshError && (
              <div className="border border-rose-200 bg-rose-50 rounded p-2 text-xs text-rose-800">
                {refreshError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRefreshOpen(false)}
              disabled={refreshRunning}
              data-testid="legal-refresh-all-close"
            >
              {refreshRunning ? "Running…" : "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One filter bar (Dean, 15 Oct 2026): field · operator · value, AND / OR groups, chips — the same on Tracker, Calendar, Agenda and Unassigned */}
      <Card>
        <CardContent className="p-2.5 flex items-start gap-2 flex-wrap">
          <div className="relative w-[260px] shrink-0">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search SF# / company / director / case no. / court…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
              data-testid="legal-search"
            />
          </div>
          {/* Quick-chip row retired (Dean, 15 Oct 2026) — My cases / No legal owner / Overdue / Hearings / Needs attention / lanes are shared saved views now */}
          <FilterBar fields={fields} tree={lv.tree} onChange={lv.setTree} testid="legal-filter" className="flex-1 min-w-[280px]">
        <label
          className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all cursor-pointer select-none ${includeHistory
            ? "bg-slate-100 border-slate-400 text-slate-900"
            : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
          title={`Live legal hides closed, written-off, history-only and tidy-up files (LoanDisk says Legal, nothing on file). Switch on to see them too — ${summary.history_count || 0} file(s): ${Object.entries(summary.live_breakdown || {}).filter(([k]) => k !== "live").map(([k, n]) => `${n} ${({ tidy_up: "tidy up", history: "history", closed: "closed", none: "no legal work" })[k] || k}`).join(" · ") || "none"}`}
          data-testid="legal-include-history"
        >
          <Archive className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">Include history</span>
          <span className="text-[11px] font-bold tabular-nums px-1 rounded bg-white/70 border border-slate-200" data-testid="legal-history-count">{summary.history_count || 0}</span>
          <Switch checked={includeHistory} onCheckedChange={setIncludeHistory} className="scale-75 -my-1" data-testid="legal-include-history-switch" />
        </label>
            {(handlerFilter !== "all" || attentionOnly || lane || quickView || search || includeHistory || actionsWithinDays !== null) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[11px] text-slate-500"
                onClick={() => { setHandlerFilter("all"); setAttentionOnly(false); setLane(null); setQuickView(null); setSearch(""); setIncludeHistory(false); setActionsWithinDays(null); }}
                data-testid="legal-filter-reset"
              >
                Reset quick filters
              </Button>
            )}
          </FilterBar>
        </CardContent>
      </Card>

      {/* Item 12: Bulk action bar — appears only when rows are selected */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-2.5 bg-indigo-50 border-2 border-indigo-200 rounded-md text-sm"
          data-testid="legal-bulk-bar"
        >
          <span className="font-semibold text-indigo-900">
            {selectedIds.size} selected
          </span>
          <Select disabled={bulkBusy} onValueChange={(v) => handleBulkAssign(v === "_unassign" ? null : v, "legal_handler")}>
            <SelectTrigger className="w-[220px] h-8 bg-white" data-testid="legal-bulk-assign-select">
              <SelectValue placeholder={bulkBusy ? "Assigning…" : "⚖ Set legal owner…"} />
            </SelectTrigger>
            <SelectContent>
              {handlers.map(h => (
                <SelectItem key={h} value={h} data-testid={`legal-bulk-handler-${h}`}>{h}</SelectItem>
              ))}
              <SelectItem value="_unassign" data-testid="legal-bulk-handler-unassign">— Clear legal owner —</SelectItem>
            </SelectContent>
          </Select>
          <Select disabled={bulkBusy} onValueChange={(v) => handleBulkAssign(v === "_unassign" ? null : v, "handler")}>
            <SelectTrigger className="w-[210px] h-8 bg-white" data-testid="legal-bulk-account-select">
              <SelectValue placeholder={bulkBusy ? "Assigning…" : "Account handler…"} />
            </SelectTrigger>
            <SelectContent>
              {accountHandlers.map(h => (
                <SelectItem key={h} value={h} data-testid={`legal-bulk-account-${h}`}>{h}</SelectItem>
              ))}
              <SelectItem value="_unassign" data-testid="legal-bulk-account-unassign">— Unassign —</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs ml-auto"
            onClick={() => setSelectedIds(new Set())}
            data-testid="legal-bulk-clear"
          >
            Clear selection
          </Button>
        </div>
      )}

      {/* Cases table */}
      <Card>
        <CardHeader className="py-2.5 px-4 border-b flex flex-row items-center">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            {loading ? "Loading…" : `${visibleCases.length} cases`}
            {!loading && visibleCases.length !== cases.length && <span className="text-xs text-muted-foreground font-normal">of {cases.length} loaded</span>}
          </CardTitle>
          <span className="ml-auto text-[11px] text-muted-foreground" data-testid="legal-sort-label">
            Sorted by {({ customer: "customer", journey: "journey", next_action: "next action due (overdue first)", owners: "owners", latest_note: "latest note", handler: "handler", account: "account handler", legal_owner: "legal owner", balance: "balance" })[sort.key]} {sort.dir === "asc" ? "↑" : "↓"}
          </span>
        </CardHeader>
        <CardContent className="p-0">
          {loading && cases.length === 0 ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : visibleCases.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              No legal cases match the current filters.
              <div className="mt-2 text-xs">
                {quickView ? "Clear the quick view above to see the rest." : <>Try clicking <strong>Refresh All</strong> above to import existing legal cases.</>}
              </div>
            </div>
          ) : (
            <table className="w-full text-sm border-collapse" data-testid="legal-tracker-grid">
              <thead className="bg-[#f5f6f8] text-[11px] text-slate-600 sticky top-0 z-10">
                <tr className="border-b border-slate-300">
                  <th className="w-12 px-2 py-1.5 border-r border-slate-200 text-center text-[10px] text-slate-400 font-normal" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={toggleAllVisible}
                      aria-label="Select all visible cases"
                      data-testid="legal-select-all"
                    />
                  </th>
                  {AT_COLUMNS.map(([k, label, align, Icon, width]) => (
                    <th key={k} className={`${align} ${width} px-3 py-1.5 font-medium cursor-pointer select-none border-r border-slate-200 last:border-r-0 hover:bg-slate-200/60 ${sort.key === k ? "text-slate-900" : ""}`}
                      onClick={() => toggleSort(k)} data-testid={`legal-sort-${k}`} title={`Sort by ${label.toLowerCase()}`}>
                      <span className={`inline-flex items-center gap-1.5 ${align === "text-right" ? "flex-row-reverse" : ""}`}><Icon className="w-3.5 h-3.5 text-slate-400" />{label} <SortIcon k={k} /></span>
                    </th>
                  ))}
                  <th className="w-10 px-2 border-l border-slate-200"></th>
                </tr>
              </thead>
              <tbody>
                {visibleCases.map((c, idx) => {
                  const isSel = selectedIds.has(c.case_id);
                  const open = () => navigate(`/legal/case/${c.case_id}?source=legal`);
                  const ne = c.next_event;
                  const st = ne ? (ne.sla_breached ? "breached" : ne.priority || "upcoming") : "none";
                  return (
                  <tr
                    key={c.case_id}
                    className={`group border-b border-slate-200 hover:bg-[#f0f4ff] transition-colors h-[52px] ${isSel ? "bg-indigo-50/60" : "bg-white"}`}
                    data-testid={`legal-case-row-${c.case_id}`}
                  >
                    <td
                      className="px-2 py-1.5 cursor-pointer border-r border-slate-200 text-center align-middle"
                      onClick={(e) => { e.stopPropagation(); toggleRow(c.case_id); }}
                      data-testid={`legal-row-checkbox-${c.case_id}`}
                    >
                      <span className={`text-[10px] tabular-nums text-slate-400 ${isSel ? "hidden" : "group-hover:hidden"}`}>{idx + 1}</span>
                      <Checkbox
                        checked={isSel}
                        aria-label={`Select ${c.customer_name}`}
                        className={`pointer-events-none mx-auto ${isSel ? "" : "hidden group-hover:flex"}`}
                      />
                    </td>
                    <td className="px-3 py-1.5 cursor-pointer border-r border-slate-200 align-middle" onClick={open}>
                      <div className="font-medium text-slate-900 truncate max-w-[280px] flex items-center gap-1.5">
                        <span className="truncate">{c.customer_name || "—"}</span>
                        <span className="font-mono text-[10px] text-muted-foreground font-normal shrink-0">{c.loan_application_id || ""}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[11px] text-muted-foreground truncate max-w-[150px]">{c.borrower_director_name || ""}</span>
                        <CompactBadges c={c} accountStatuses={accountStatuses} />
                      </div>
                      {/* Elle (9bb64edd60): case no. & court under each account — click to edit */}
                      <div onClick={(e) => e.stopPropagation()}><CourtReferenceInline caseRow={c} /></div>
                    </td>
                    <td className="px-3 py-1.5 cursor-pointer border-r border-slate-200 align-middle" onClick={open} data-testid={`legal-journey-${c.case_id}`}>
                      <JourneyLine journey={c.journey} caseId={c.case_id} />
                      <LivePill journey={c.journey} caseId={c.case_id} muted={!includeHistory} />
                      {c.legal_closure?.type === "closed_review" && c.legal_closure.review_at && (
                        <span
                          className={`inline-block mt-0.5 text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border ${
                            c.legal_closure.review_at <= new Date().toISOString().slice(0, 10)
                              ? "bg-red-50 text-red-700 border-red-300"
                              : "bg-amber-50 text-amber-800 border-amber-300"
                          }`}
                          title={c.legal_closure.reason || "Closed — diarised for a future review"}
                          data-testid={`legal-closedreview-badge-${c.case_id}`}
                        >
                          ⏰ Review {formatShortDate(c.legal_closure.review_at)}
                        </span>
                      )}
                    </td>
                    {/* Owners — account handler + legal owner in one cell, click to change */}
                    <td className="px-3 py-1.5 border-r border-slate-200 align-middle" data-testid={`legal-owners-cell-${c.case_id}`}>
                      <OwnersCell c={c} accountHandlers={summary.handler_options} onChanged={reload} />
                    </td>
                    {/* Next action — one status colour per action (green · amber · red · breached); day count + SLA on hover */}
                    <td className="px-3 py-1.5 cursor-pointer border-r border-slate-200 align-middle" onClick={open} data-testid={`legal-next-action-${c.case_id}`}>
                      {["ready_for_handover", "pre_action"].includes(c.journey?.legal?.key) && c.journey?.account?.key !== "legal" && (
                        <div className="mb-1" onClick={(e) => e.stopPropagation()}>
                          <HandToLegalButton caseRow={c} journey={c.journey} onDone={() => reload()} />
                        </div>
                      )}
                      {ne ? (
                        <div className={`inline-flex items-center gap-2 pl-2 pr-2.5 py-1 rounded-md border max-w-full ${NEXT_TONE[st]}`}
                          title={`${ne.label} · due ${formatShortDate(ne.date)}${ne.days_overdue > 0 ? ` · ${ne.days_overdue} day${ne.days_overdue === 1 ? "" : "s"} overdue` : ne.priority === "green" ? " · due today" : ""}${ne.sla_breached ? ` · SLA breached${ne.sla_deadline ? ` (deadline ${formatShortDate(ne.sla_deadline)})` : ""}` : ne.sla_deadline ? ` · SLA by ${formatShortDate(ne.sla_deadline)}` : ""}${ne.mandatory ? " · mandatory" : ""}${ne.owner ? ` · ${ne.owner}` : ""}`}
                          data-testid={`legal-next-status-${st}-${c.case_id}`}>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${NEXT_DOT[st]}`} />
                          <span className="min-w-0">
                            <span className="block text-[11px] font-medium truncate max-w-[200px]">{ne.label}</span>
                            <span className="block text-[10px] tabular-nums opacity-80">{formatShortDate(ne.date)}{ne.owner ? ` · ${ne.owner.split(" ")[0]}` : ""}</span>
                          </span>
                        </div>
                      ) : <span className="inline-flex items-center gap-2 pl-2 pr-2.5 py-1 rounded-md border border-dashed border-red-300 text-[11px] italic text-red-700/80" title="Nothing diarised — this file will not prompt anyone">No next action</span>}
                      {c.hearing && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-800 border-indigo-200"
                          title={`Court hearing ${formatShortDate(c.hearing.date)} — ${c.hearing.prep_done}/${c.hearing.prep_total} prep tasks complete`}
                          data-testid={`legal-hearing-chip-${c.case_id}`}>
                          <Landmark className="w-2.5 h-2.5" />
                          {formatShortDate(c.hearing.date)}
                          {c.hearing.prep_total > 0 && (
                            <span className={c.hearing.prep_done < c.hearing.prep_total ? "text-amber-700" : "text-emerald-700"}>
                              · prep {c.hearing.prep_done}/{c.hearing.prep_total}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums cursor-pointer align-middle" onClick={open}>
                      {fmtGBP(c.balance_amount)}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground border-l border-slate-200 align-middle" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-slate-100" data-testid={`legal-row-menu-${c.case_id}`} title="More">
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="text-xs">
                          <DropdownMenuItem onClick={open} data-testid={`legal-row-open-${c.case_id}`}><ArrowRight className="w-3.5 h-3.5 mr-2" /> Open case</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/legal/case/${c.case_id}?source=legal&tab=p2ps`)} data-testid={`legal-row-schedule-${c.case_id}`}><CalIcon className="w-3.5 h-3.5 mr-2" /> Legal schedule</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setBundleCase(c)} data-testid={`legal-court-bundle-btn-${c.case_id}`}><FileText className="w-3.5 h-3.5 mr-2" /> Court bundle (full notes)</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
    </TooltipProvider>
  );
}

// Dean, 8 Oct 2026 — the badge pile under the customer name becomes one compact row: first two chips + "+N" (hover lists the rest)
// 13 Oct 2026: the status / legal-hold chips are gone — the Journey column carries both axes.
function CompactBadges({ c, accountStatuses }) {
  const chips = [];
  if (c.handling_marker === "out_of_jurisdiction") chips.push({ key: "ooj", label: "🌍 Out of jurisdiction", tone: "bg-sky-50 text-sky-800 border-sky-300", title: "Out of jurisdiction (Scottish / NI) — limited actions", testid: `legal-ooj-badge-${c.case_id}` });
  if (c.handling_marker === "external") chips.push({ key: "ext", label: `🏛 External${c.external_firm ? ` · ${c.external_firm}` : ""}`, tone: "bg-indigo-50 text-indigo-800 border-indigo-300", title: c.external_firm ? `Handled externally by ${c.external_firm}` : "Handled externally", testid: `legal-external-badge-${c.case_id}` });
  if (c.breathing_space?.active) chips.push({ key: "bs", label: `⏸ BS Protected${c.breathing_space.active_count > 1 ? ` ×${c.breathing_space.active_count}` : ""}`, tone: "bg-purple-50 text-purple-700 border-purple-300", title: `Breathing Space active — do not contact${c.breathing_space.end_date ? ` · ends ${c.breathing_space.end_date}` : ""}`, testid: `legal-bs-badge-${c.case_id}` });
  if ((c.pg_insolvencies || []).length) chips.push({ key: "pg", label: "⚠ PG insolvency", tone: "bg-rose-50 text-rose-800 border-rose-300", title: "A guarantor is bankrupt / in an IVA — enforcement against them is blocked", testid: `legal-pg-badge-${c.case_id}` });
  for (const k of c.manual_statuses || []) {
    const s = (accountStatuses || []).find(x => x.key === k);
    chips.push({ key: `st-${k}`, label: s?.label || k, tone: "bg-slate-50 text-slate-700 border-slate-300", title: s?.label || k, testid: `legal-status-tag-${c.case_id}-${k}` });
  }
  if (!chips.length) return null;
  const shown = chips.slice(0, 2), rest = chips.slice(2);
  return (
    <div className="flex items-center gap-1 mt-1 flex-nowrap min-w-0" data-testid={`legal-badges-${c.case_id}`}>
      {shown.map(ch => ch.hover ? (
        <Tooltip key={ch.key}>
          <TooltipTrigger asChild>
            <span className={`inline-flex items-center text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap truncate max-w-[150px] cursor-help ${ch.tone}`} data-testid={ch.testid}>{ch.label}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="text-xs">{ch.hover}</TooltipContent>
        </Tooltip>
      ) : (
        <span key={ch.key} className={`inline-flex items-center text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap truncate max-w-[150px] ${ch.tone}`} title={ch.title} data-testid={ch.testid}>{ch.label}</span>
      ))}
      {rest.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center text-[9px] font-semibold px-1.5 py-0.5 rounded border bg-white text-slate-600 border-slate-300 cursor-default" data-testid={`legal-badges-more-${c.case_id}`}>+{rest.length}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs"><div className="space-y-0.5">{rest.map(ch => <div key={ch.key}>{ch.label}</div>)}</div></TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

const humanise = (key) => (key || "").replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());

// "2026-07-04" → "4 Jul" (compact for the Next-action column)
function formatShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.length > 10 ? iso : iso + "T00:00:00");
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// Emma 7cb2874553 — full case notes + raw Airtable text (court-bundle view)
// accessible straight from the tracker, mirroring the ageing account page.
function CourtBundleDialog({ caseRow, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!caseRow) return;
    setData(null); setErr(null); setShowRaw(false); setLoading(true);
    getLegalNotesBundle(caseRow.case_id)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [caseRow]);

  const entries = data?.timeline || [];
  const blob = (data?.case_notes || "").trim();

  const copyBlob = async () => {
    try {
      await navigator.clipboard.writeText(blob);
      toast.success("Raw notes copied — ready for the court bundle");
    } catch {
      toast.error("Couldn't copy — select the text manually");
    }
  };

  return (
    <Dialog open={!!caseRow} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="legal-court-bundle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-700" />
            Case notes — {caseRow?.customer_name || caseRow?.case_id}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading notes…
          </div>
        ) : err ? (
          <div className="text-sm text-rose-700 py-4" data-testid="court-bundle-error">{err}</div>
        ) : (
          <div className="space-y-3">
            {entries.length === 0 && !blob && (
              <div className="text-sm italic text-slate-400 py-4">No notes recorded for this case yet.</div>
            )}
            {entries.length > 0 && (
              <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="court-bundle-entries">
                {[...entries].sort((a, b) => (b.date_added || "").localeCompare(a.date_added || "")).map((e, idx) => (
                  <li key={e.id || idx} className="border rounded-md px-2.5 py-1.5 bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="outline" className="text-[9px]">{(e.entry_type || "note").replace(/_/g, " ")}</Badge>
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {e.date_added || ""}{e.effective_date ? ` · ➜ ${e.effective_date}` : ""}
                      </span>
                    </div>
                    {e.notes && <p className="text-[12px] text-slate-700 whitespace-pre-wrap leading-snug mt-1">{e.notes}</p>}
                  </li>
                ))}
              </ul>
            )}
            {blob && (
              <div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowRaw(v => !v)}
                    className="text-[11px] text-amber-700 hover:underline"
                    data-testid="court-bundle-toggle-raw"
                  >
                    {showRaw ? "Hide raw text" : "Show raw Airtable text (court-bundle view)"}
                  </button>
                  {showRaw && (
                    <button type="button" onClick={copyBlob} className="text-[11px] text-blue-700 hover:underline" data-testid="court-bundle-copy-raw">
                      Copy raw text
                    </button>
                  )}
                </div>
                {showRaw && (
                  <div
                    data-testid="court-bundle-raw-blob"
                    className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200 max-h-72 overflow-y-auto whitespace-pre-wrap leading-relaxed text-[12px]"
                  >
                    {blob}
                  </div>
                )}
              </div>
            )}
            {data?.last_synced_at && (
              <div className="text-[10px] text-muted-foreground text-right">
                Last Airtable sync: {new Date(data.last_synced_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
